<template>
    <div>
        <PageDescriptionEditor page-title="FAQ"
            default-description="Find answers to commonly asked questions about our publishing services, submissions, and more." />

        <div class="mb-6">
            <h2 class="font-header text-[3rem] font-bold text-primary">FAQ Management</h2>
        </div>

        <div v-if="showForm" class="bg-white p-6 rounded-lg shadow-md mb-6">
            <h3 class="font-header text-[1.5rem] font-bold mb-2">{{ editingFaq ? 'Edit FAQ' : 'Add New FAQ' }}</h3>
            <p v-if="!editingFaq" class="font-body text-[1.125rem] leading-[1.75] text-gray-600 mb-4">Fill in the form
                below to create a new frequently asked question.</p>
            <p v-else class="font-body text-[1.125rem] leading-[1.75] text-gray-600 mb-4">Update the FAQ details below.
            </p>
            <form @submit.prevent="saveFaq" class="space-y-4">
                <div class="grid grid-cols-2 gap-4">
                    <div class="col-span-2">
                        <div class="flex items-start gap-2">
                            <span class="text-red-500 mt-3">*</span>
                            <div class="flex-1">
                                <label class="block font-body text-[1.125rem] leading-[1.75] mb-1">Question</label>
                                <input v-model="formData.question" type="text" required
                                    class="w-full px-3 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75]"
                                    placeholder="e.g., How do I submit a manuscript?" />
                            </div>
                        </div>
                    </div>
                    <div class="col-span-2">
                        <div class="flex items-start gap-2">
                            <span class="text-red-500 mt-3">*</span>
                            <div class="flex-1">
                                <label class="block font-body text-[1.125rem] leading-[1.75] mb-1">Answer</label>
                                <div class="border border-gray-300 rounded-md">
                                    <RichTextEditor ref="answerEditor" :content="formData.answer"
                                        @update:content="formData.answer = $event"
                                        placeholder="Write the answer here..." />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="flex gap-2">
                    <button type="submit"
                        class="px-6 py-2 bg-green-700 text-white rounded-md hover:bg-green-800 transition-colors font-body text-[1.125rem] leading-[1.75]">
                        {{ editingFaq ? 'Update' : 'Create' }}
                    </button>
                    <button type="button" @click="cancelForm"
                        class="px-6 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors font-body text-[1.125rem] leading-[1.75]">
                        Cancel
                    </button>
                </div>
            </form>
        </div>

        <div class="mb-4 flex justify-end">
            <button @click="showForm = true; editingFaq = null"
                class="px-6 py-2 bg-green-700 text-white rounded-md hover:bg-green-800 transition-colors font-body text-[1.125rem] leading-[1.75]">
                Add New FAQ
            </button>
        </div>

        <div v-if="hasOrderChanges"
            class="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 flex justify-between items-center">
            <p class="font-body text-body text-yellow-800">
                You have unsaved order changes. Click "Confirm Ordering" to apply them.
            </p>
            <button @click="confirmOrdering" :disabled="savingOrder"
                class="px-6 py-2 bg-green-700 text-white rounded-md hover:bg-green-800 transition-colors font-header text-[1.125rem] disabled:opacity-50 disabled:cursor-not-allowed">
                {{ savingOrder ? 'Saving...' : 'Confirm Ordering' }}
            </button>
        </div>

        <div class="bg-white rounded-lg shadow-md overflow-hidden">
            <div class="p-4 bg-green-700 text-white">
                <p class="font-body text-[1.125rem] leading-[1.75]">Drag and drop items to reorder. Click "Confirm
                    Ordering" to save changes.</p>
            </div>
            <div v-if="loading" class="p-4 text-center">
                <Loader size="medium" />
            </div>
            <div v-else-if="faqs.length === 0"
                class="p-8 text-center text-gray-500 font-body text-[1.125rem] leading-[1.75]">
                <span>No FAQs found. Click "Add New FAQ" to create one.</span>
            </div>
            <div v-else class="divide-y divide-creme-200">
                <div v-for="(faq, index) in sortedFaqs" :key="faq.id" :draggable="true"
                    @dragstart="handleDragStart(index, $event)" @dragover.prevent="handleDragOver(index, $event)"
                    @drop="handleDrop(index, $event)" @dragend="handleDragEnd" :class="[
                        'p-4 cursor-move transition-colors',
                        draggedIndex === index ? 'bg-green-100 opacity-50' : 'hover:bg-creme-50',
                        dragOverIndex === index ? 'border-t-2 border-green-700' : ''
                    ]">
                    <div class="flex items-start justify-between gap-4">
                        <div class="flex items-start gap-4 flex-1">
                            <div class="flex-shrink-0 mt-1 md:hidden">
                                <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor"
                                    viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M4 8h16M4 16h16" />
                                </svg>
                            </div>
                            <div class="flex-1">
                                <div class="flex items-center gap-3 mb-2">
                                    <span
                                        :class="['px-2 py-1 rounded text-xs font-header', faq.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800']">
                                        {{ faq.is_active ? 'Active' : 'Inactive' }}
                                    </span>
                                </div>
                                <h4 class="font-header text-[1.5rem] font-bold text-primary mb-1">{{ faq.question }}
                                </h4>
                                <p class="font-body text-body text-gray-600 line-clamp-2">{{ stripHtml(faq.answer) }}
                                </p>
                            </div>
                        </div>
                        <div class="flex gap-2 flex-shrink-0">
                            <button @click="editFaq(faq)"
                                class="px-3 py-1 text-[1.125rem] text-green-700 hover:text-green-800 font-header">Edit</button>
                            <button @click="deleteFaq(faq.id)"
                                class="px-3 py-1 text-[1.125rem] text-red-600 hover:text-red-700 font-header">Delete</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue'
import { adminService } from '../../services/admin'
import { useScrollToTop } from '../../composables/useScrollToTop'

const { scrollToTop } = useScrollToTop()
import PageDescriptionEditor from './PageDescriptionEditor.vue'
import Loader from '../Loader.vue'
import { useLoadingState } from '../../composables/useLoadingState'
import RichTextEditor from '../common/RichTextEditor.vue'
import { convertLegacyQuillContent } from '../../utils/quillConfig'

const faqs = ref([])
const originalOrder = ref(new Map())
const showForm = ref(false)
const editingFaq = ref(null)
const draggedIndex = ref(null)
const dragOverIndex = ref(null)
const savingOrder = ref(false)
const answerEditor = ref(null)
const formData = ref({
    question: '',
    answer: convertLegacyQuillContent('')
})
const currentPage = ref(1)
const limit = ref(50)
const pagination = ref(null)
const { loading, setLoading } = useLoadingState(false)

const sortedFaqs = computed(() => {
    return [...faqs.value].sort((a, b) => {
        const orderA = typeof a.display_order === 'number' ? a.display_order : 0
        const orderB = typeof b.display_order === 'number' ? b.display_order : 0
        return orderA - orderB
    })
})

const hasOrderChanges = computed(() => {
    if (!faqs.value.length || originalOrder.value.size === 0) {
        return false
    }

    return sortedFaqs.value.some((faq, index) => {
        if (faq?.id == null) {
            return false
        }
        return originalOrder.value.get(faq.id) !== index
    })
})

const stripHtml = (html) => {
    if (!html) return ''
    const tmp = document.createElement('DIV')
    tmp.innerHTML = html
    return tmp.textContent || tmp.innerText || ''
}

const loadFaqs = async (page = 1) => {
    setLoading(true)
    try {
        const response = await adminService.getFaqs(page, limit.value)
        let fetchedFaqs = []

        if (response?.data && response?.pagination) {
            fetchedFaqs = Array.isArray(response.data) ? response.data : []
            pagination.value = response.pagination
        } else {
            const data = Array.isArray(response) ? response : (response?.data || [])
            fetchedFaqs = Array.isArray(data) ? data : []
            pagination.value = null
        }

        currentPage.value = page

        const normalizedFaqs = fetchedFaqs.map((faq, index) => ({
            ...faq,
            display_order: typeof faq.display_order === 'number' ? faq.display_order : index
        }))

        faqs.value = normalizedFaqs
        draggedIndex.value = null
        dragOverIndex.value = null
        originalOrder.value.clear()
        sortedFaqs.value.forEach((faq, index) => {
            if (faq?.id != null) {
                originalOrder.value.set(faq.id, index)
            }
        })
    } catch (error) {
        console.error('Failed to load FAQs:', error)
        faqs.value = []
        pagination.value = null
        draggedIndex.value = null
        dragOverIndex.value = null
        originalOrder.value.clear()
    } finally {
        setLoading(false)
    }
}

const handleDragStart = (index, event) => {
    draggedIndex.value = index
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/html', event.target.outerHTML)
}

const handleDragOver = (index, event) => {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
    dragOverIndex.value = index
}

const handleDrop = (index, event) => {
    event.preventDefault()

    if (draggedIndex.value === null || draggedIndex.value === index) {
        dragOverIndex.value = null
        return
    }

    const draggedFaq = sortedFaqs.value[draggedIndex.value]
    const newFaqs = [...sortedFaqs.value]

    newFaqs.splice(draggedIndex.value, 1)
    newFaqs.splice(index, 0, draggedFaq)

    newFaqs.forEach((faq, idx) => {
        faq.display_order = idx
    })

    faqs.value = newFaqs
    dragOverIndex.value = null
}

const handleDragEnd = () => {
    draggedIndex.value = null
    dragOverIndex.value = null
}

const confirmOrdering = async () => {
    savingOrder.value = true
    try {
        const updates = sortedFaqs.value.map(faq => ({
            id: faq.id,
            display_order: faq.display_order
        }))

        await Promise.all(
            updates.map(update =>
                adminService.updateFaq(update.id, { display_order: update.display_order })
            )
        )

        await loadFaqs(currentPage.value)
    } catch (error) {
        console.error('Failed to save order:', error)
        alert('Failed to save order. Please try again.')
    } finally {
        savingOrder.value = false
    }
}

const editFaq = async (faq) => {
    try {
        const fullFaq = await adminService.getFaq(faq.id)
        editingFaq.value = fullFaq
        formData.value = {
            question: fullFaq.question,
            answer: convertLegacyQuillContent(fullFaq.answer || '')
        }
        showForm.value = true
        await nextTick()
        if (answerEditor.value) {
            answerEditor.value.setHTML(formData.value.answer || '')
        }
    } catch (error) {
        console.error('Failed to load FAQ:', error)
        alert('Failed to load FAQ details')
    }
}

const cancelForm = async () => {
    showForm.value = false
    editingFaq.value = null
    formData.value = {
        question: '',
        answer: convertLegacyQuillContent('')
    }
    await nextTick()
    if (answerEditor.value) {
        answerEditor.value.setHTML(convertLegacyQuillContent(''))
    }
    scrollToTop()
}

const saveFaq = async () => {
    try {
        const data = {
            question: (formData.value.question || '').trim(),
            answer: (formData.value.answer || '').trim()
        }

        if (editingFaq.value) {
            await adminService.updateFaq(editingFaq.value.id, data)
        } else {
            const maxOrder = faqs.value.length > 0
                ? Math.max(...faqs.value.map(f => f.display_order || 0))
                : -1
            data.display_order = maxOrder + 1
            await adminService.createFaq(data)
        }

        await loadFaqs(currentPage.value)
        cancelForm()
        scrollToTop()
    } catch (error) {
        console.error('Failed to save FAQ:', error)
        alert('Failed to save FAQ. Please try again.')
    }
}

const deleteFaq = async (id) => {
    if (!confirm('Are you sure you want to delete this FAQ?')) return
    try {
        await adminService.deleteFaq(id)
        await loadFaqs(currentPage.value)
    } catch (error) {
        console.error('Failed to delete FAQ:', error)
        alert('Failed to delete FAQ. Please try again.')
    }
}

onMounted(() => {
    loadFaqs(1)
})
</script>

<style scoped>
.line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

:deep(.ql-editor) {
    min-height: 150px;
    line-height: 1.75;
    color: #253439;
    background-color: #FFFFFF !important;
    direction: ltr !important;
    text-align: left !important;
    unicode-bidi: embed !important;
}

:deep(.ql-container) {
    font-family: 'Manrope', sans-serif;
    background-color: #FFFFFF !important;
    direction: ltr !important;
    unicode-bidi: embed !important;
}

:deep(.ql-toolbar) {
    border-top-left-radius: 0.375rem;
    border-top-right-radius: 0.375rem;
    background-color: #FFFFFF !important;
}

:deep(.ql-editor *) {
    direction: ltr !important;
}

input[type="checkbox"] {
    background-color: #FFFFFF !important;
    appearance: checkbox;
    -webkit-appearance: checkbox;
    -moz-appearance: checkbox;
}

input[type="checkbox"]:checked {
    background-color: #FFFFFF !important;
    accent-color: #2D4D31;
}
</style>
